import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Lock, Mail, User, Loader2, ShieldCheck, RotateCcw } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { register, login, googleSignIn, formatApiError } from "../lib/authApi";
import { useAuth } from "../context/AuthContext";
import { trackSignup } from "../lib/analytics";
import ForgotPasswordModal from "./ForgotPasswordModal";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH — the browser sends its origin automatically.

export const AuthModal = ({ open, onClose, onSuccess, headline, subheadline }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("register"); // register | login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [googleIframeReady, setGoogleIframeReady] = useState(false);
  const googleWrapRef = useRef(null);

  // After the Google `<GoogleLogin>` component mounts an iframe, poll briefly
  // to check whether Google's gsi/button iframe rendered at a real size. If it
  // stays 0-height (blocked, third-party cookies off, extension interference),
  // we surface a fallback custom button that pops the One Tap chooser.
  useEffect(() => {
    if (!open) return;
    let attempts = 0;
    const timer = setInterval(() => {
      const iframe = googleWrapRef.current?.querySelector("iframe");
      const h = iframe?.getBoundingClientRect().height || 0;
      if (h >= 30) {
        setGoogleIframeReady(true);
        clearInterval(timer);
      } else if (++attempts >= 12) {
        // ~3 seconds — give up and show the fallback
        clearInterval(timer);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [open, mode]);

  const reset = () => {
    setError("");
  };

  const finish = (res) => {
    setUser(res.user);
    onSuccess?.(res.user);
  };

  const submit = async (e) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      const isRegister = mode === "register";
      const res = isRegister
        ? await register(email.trim().toLowerCase(), password, name.trim())
        : await login(email.trim().toLowerCase(), password);
      if (isRegister) trackSignup("email");
      finish(res);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async (response) => {
    reset();
    if (!response?.credential) {
      setError("Google sign-in did not return a credential. Please retry.");
      return;
    }
    setBusy(true);
    try {
      const res = await googleSignIn(response.credential);
      trackSignup("google");
      finish(res);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto"
      data-testid="auth-modal-overlay"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center">
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-4"
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
        >
          <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                {headline || (mode === "register" ? "Create your free account" : "Welcome back")}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {subheadline ||
                  "Login is only needed to chat with your policy. Everything else stays anonymous."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="auth-close"
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto">
          {/* Google — always at the top so it's the first choice a user sees. */}
          <div className="text-[11px] font-medium tracking-widest uppercase text-slate-400 mb-2 text-center">
            Continue with Google
          </div>
          <div
            ref={googleWrapRef}
            className="w-full flex flex-col items-center justify-center min-h-[46px]"
            data-testid="google-signin-wrap"
          >
            <GoogleLogin
              onSuccess={onGoogle}
              onError={() => setError("Google sign-in was cancelled or failed. Try email below.")}
              theme="outline"
              size="large"
              shape="pill"
              text={mode === "register" ? "signup_with" : "signin_with"}
              width="320"
              use_fedcm_for_prompt
            />
            {/* Fallback shown only if Google's iframe never renders at a
                real height (blocked iframe, 3rd-party cookies off, etc). */}
            {!googleIframeReady && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="google-fallback-hint"
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
              >
                <RotateCcw className="h-3 w-3" />
                Google button not showing? Refresh
              </button>
            )}
          </div>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 tracking-wider uppercase">
              or with email
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Name (optional)</span>
                <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                  <User className="h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                    placeholder="Priya Ramanathan"
                    data-testid="auth-name"
                  />
                </div>
              </label>
            )}
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Email</span>
              <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                <Mail className="h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                  placeholder="you@example.com"
                  data-testid="auth-email"
                />
              </div>
            </label>
            <label className="block">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">Password</span>
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setForgotOpen(true);
                    }}
                    data-testid="auth-forgot-link"
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                <Lock className="h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={mode === "register" ? 8 : 1}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                  placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                  data-testid="auth-password"
                />
              </div>
            </label>

            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-full py-2.5 text-sm font-medium shadow-sm"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "register" ? "Create account & continue" : "Log in & continue"}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-slate-500">
            {mode === "register" ? (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => {
                    setMode("login");
                    reset();
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="switch-to-login"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                New here?{" "}
                <button
                  onClick={() => {
                    setMode("register");
                    reset();
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="switch-to-register"
                >
                  Create an account
                </button>
              </>
            )}
          </div>

          <p className="mt-4 text-[11px] text-slate-400 text-center leading-relaxed" data-testid="auth-terms-note">
            We only use your email for account access. No spam, no third-party sharing. By continuing you accept our{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
              data-testid="auth-terms-link"
            >
              Terms &amp; Disclaimer
            </a>
            .
          </p>
        </div>
      </div>
      </div>

      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        onBackToLogin={() => setForgotOpen(false)}
        defaultEmail={email}
      />
    </div>,
    document.body,
  );
};

export default AuthModal;
